// Global Error Handling Utility

export type ErrorType =
  | "network"
  | "auth"
  | "server"
  | "validation"
  | "unknown";

export interface AppError {
  message: string;
  type: ErrorType;
  status?: number;
  statusText?: string;
  originalError?: any;
}

export interface ErrorDisplayConfig {
  showRetry?: boolean;
  showLogin?: boolean;
  customActions?: Array<{
    label: string;
    action: () => void;
    variant?: "primary" | "secondary" | "neutral-tertiary";
  }>;
}

// Error type configurations
export const ERROR_CONFIGS: Record<ErrorType, ErrorDisplayConfig> = {
  network: {
    showRetry: true,
  },
  auth: {
    showLogin: true,
  },
  server: {
    showRetry: true,
  },
  validation: {
    showRetry: false,
  },
  unknown: {
    showRetry: true,
  },
};

// Error messages for different scenarios
export const ERROR_MESSAGES = {
  network: {
    connection:
      "Unable to connect to the server. Please check your internet connection and try again.",
    timeout: "Request timed out. Please try again.",
    offline: "You appear to be offline. Please check your internet connection.",
  },
  auth: {
    expired: "Your session has expired. Please log in again to continue.",
    forbidden:
      "You do not have permission to perform this action. Please contact your administrator.",
    unauthorized: "Authentication required. Please log in to continue.",
  },
  server: {
    unavailable: "Service is temporarily unavailable. Please try again later.",
    error: "Server error occurred. Please try again in a few moments.",
    maintenance: "Service is under maintenance. Please try again later.",
  },
  validation: {
    invalidData:
      "Invalid data provided. Please check your information and try again.",
    duplicate:
      "A record with this information already exists. Please check your details.",
    required: "Please fill in all required fields.",
    format: "Please check the format of your input.",
  },
  unknown: {
    default: "An unexpected error occurred. Please try again.",
    parse: "Failed to process the response. Please try again.",
  },
};

/**
 * Main error handling function that categorizes and formats errors
 */
export const handleError = (err: any, context?: string): AppError => {
  console.error(`Error in ${context || "application"}:`, err);

  // Network errors
  if (err.name === "TypeError" && err.message.includes("fetch")) {
    return {
      message: ERROR_MESSAGES.network.connection,
      type: "network",
      originalError: err,
    };
  }

  if (err.name === "AbortError") {
    return {
      message: ERROR_MESSAGES.network.timeout,
      type: "network",
      originalError: err,
    };
  }

  // HTTP status code errors
  if (err.status) {
    switch (err.status) {
      case 401:
        return {
          message: ERROR_MESSAGES.auth.expired,
          type: "auth",
          status: err.status,
          statusText: err.statusText,
          originalError: err,
        };

      case 403:
        return {
          message: ERROR_MESSAGES.auth.forbidden,
          type: "auth",
          status: err.status,
          statusText: err.statusText,
          originalError: err,
        };

      case 404:
        return {
          message: ERROR_MESSAGES.server.unavailable,
          type: "server",
          status: err.status,
          statusText: err.statusText,
          originalError: err,
        };

      case 409:
        return {
          message: ERROR_MESSAGES.validation.duplicate,
          type: "validation",
          status: err.status,
          statusText: err.statusText,
          originalError: err,
        };

      case 422:
        return {
          message: ERROR_MESSAGES.validation.invalidData,
          type: "validation",
          status: err.status,
          statusText: err.statusText,
          originalError: err,
        };

      case 429:
        return {
          message: "Too many requests. Please wait a moment and try again.",
          type: "server",
          status: err.status,
          statusText: err.statusText,
          originalError: err,
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          message: ERROR_MESSAGES.server.error,
          type: "server",
          status: err.status,
          statusText: err.statusText,
          originalError: err,
        };

      default:
        if (err.status >= 400 && err.status < 500) {
          return {
            message: ERROR_MESSAGES.validation.invalidData,
            type: "validation",
            status: err.status,
            statusText: err.statusText,
            originalError: err,
          };
        }

        if (err.status >= 500) {
          return {
            message: ERROR_MESSAGES.server.error,
            type: "server",
            status: err.status,
            statusText: err.statusText,
            originalError: err,
          };
        }
    }
  }

  // Custom error messages
  if (err.message && typeof err.message === "string") {
    // Check for specific error patterns
    if (err.message.toLowerCase().includes("network")) {
      return {
        message: ERROR_MESSAGES.network.connection,
        type: "network",
        originalError: err,
      };
    }

    if (err.message.toLowerCase().includes("timeout")) {
      return {
        message: ERROR_MESSAGES.network.timeout,
        type: "network",
        originalError: err,
      };
    }

    if (
      err.message.toLowerCase().includes("authentication") ||
      err.message.toLowerCase().includes("unauthorized")
    ) {
      return {
        message: ERROR_MESSAGES.auth.expired,
        type: "auth",
        originalError: err,
      };
    }

    if (
      err.message.toLowerCase().includes("forbidden") ||
      err.message.toLowerCase().includes("permission")
    ) {
      return {
        message: ERROR_MESSAGES.auth.forbidden,
        type: "auth",
        originalError: err,
      };
    }

    if (
      err.message.toLowerCase().includes("duplicate") ||
      err.message.toLowerCase().includes("already exists")
    ) {
      return {
        message: ERROR_MESSAGES.validation.duplicate,
        type: "validation",
        originalError: err,
      };
    }

    if (
      err.message.toLowerCase().includes("invalid") ||
      err.message.toLowerCase().includes("validation")
    ) {
      return {
        message: ERROR_MESSAGES.validation.invalidData,
        type: "validation",
        originalError: err,
      };
    }

    // Return the original message if it's user-friendly
    return {
      message: err.message,
      type: "unknown",
      originalError: err,
    };
  }

  // Fallback for completely unknown errors
  return {
    message: ERROR_MESSAGES.unknown.default,
    type: "unknown",
    originalError: err,
  };
};

/**
 * Get error display configuration for a specific error type
 */
export const getErrorConfig = (errorType: ErrorType): ErrorDisplayConfig => {
  return ERROR_CONFIGS[errorType] || ERROR_CONFIGS.unknown;
};

/**
 * Get color classes for error display based on error type
 */
export const getErrorColors = (errorType: ErrorType) => {
  const colorMap = {
    network: {
      background: "bg-orange-100",
      icon: "text-orange-500",
      text: "text-orange-600",
    },
    auth: {
      background: "bg-red-100",
      icon: "text-red-500",
      text: "text-red-600",
    },
    server: {
      background: "bg-yellow-100",
      icon: "text-yellow-500",
      text: "text-yellow-600",
    },
    validation: {
      background: "bg-red-100",
      icon: "text-red-500",
      text: "text-red-600",
    },
    unknown: {
      background: "bg-red-100",
      icon: "text-red-500",
      text: "text-red-600",
    },
  };

  return colorMap[errorType] || colorMap.unknown;
};

/**
 * Get appropriate icon for error type
 */
export const getErrorIcon = (errorType: ErrorType) => {
  const iconMap = {
    network: "wifi-off",
    auth: "lock",
    server: "server",
    validation: "alert-triangle",
    unknown: "alert-circle",
  };

  return iconMap[errorType] || iconMap.unknown;
};

/**
 * Create a standardized error response for API calls
 */
export const createApiError = (
  message: string,
  status: number,
  statusText?: string
): Error => {
  const error = new Error(message);
  (error as any).status = status;
  (error as any).statusText = statusText;
  return error;
};

/**
 * Check if error is retryable
 */
export const isRetryableError = (error: AppError): boolean => {
  return error.type === "network" || error.type === "server";
};

/**
 * Check if error requires authentication
 */
export const requiresAuth = (error: AppError): boolean => {
  return error.type === "auth";
};

/**
 * Get user-friendly error message for display
 */
export const getUserFriendlyMessage = (error: AppError): string => {
  return error.message;
};

/**
 * Log error for debugging purposes
 */
export const logError = (error: AppError, context?: string) => {
  const logData = {
    context: context || "application",
    type: error.type,
    message: error.message,
    status: error.status,
    statusText: error.statusText,
    timestamp: new Date().toISOString(),
    originalError: error.originalError,
  };

  console.error("Application Error:", logData);

  // In production, you might want to send this to an error tracking service
  // like Sentry, LogRocket, etc.
};
